/**
 * The handle between the message list and the reading pane. Drag it, or
 * focus it and use the arrow keys (Home and End for the limits); a
 * double-click puts it back. The size is measured against the mail area it
 * divides, so the folders beside it do not count.
 */

import { useRef } from 'react';

import {
  clamp,
  DEFAULT_LAYOUT,
  MIN_LIST_HEIGHT,
  MIN_LIST_WIDTH,
  MIN_READER_HEIGHT,
  MIN_READER_WIDTH,
  setLayout,
} from './layout';

const STEP = 16;

export function Splitter({
  direction,
  size,
}: {
  /** `right`: the list is left of it (resizes width); `bottom`: above it (height). */
  direction: 'right' | 'bottom';
  size: number;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const wide = direction === 'right';

  /** The limits, from the mail area as it is now. */
  const limits = () => {
    const area = ref.current?.parentElement?.getBoundingClientRect();
    const whole = area ? (wide ? area.width : area.height) : 0;
    const min = wide ? MIN_LIST_WIDTH : MIN_LIST_HEIGHT;
    const max = whole
      ? whole - (wide ? MIN_READER_WIDTH : MIN_READER_HEIGHT)
      : Number.MAX_SAFE_INTEGER;
    return { min, max: Math.max(min, max) };
  };
  const set = (value: number) => {
    const { min, max } = limits();
    const next = Math.round(clamp(value, min, max));
    setLayout(wide ? { listWidth: next } : { listHeight: next });
  };
  const { min, max } = limits();

  return (
    <div
      ref={ref}
      className={`splitter ${wide ? 'splitter-x' : 'splitter-y'}`}
      role="separator"
      tabIndex={0}
      aria-label="Resize the message list and reading pane"
      aria-orientation={wide ? 'vertical' : 'horizontal'}
      aria-valuenow={size}
      aria-valuemin={min}
      aria-valuemax={max === Number.MAX_SAFE_INTEGER ? undefined : max}
      title="Drag to resize. Double-click to reset."
      onPointerDown={(e) => {
        // The main button, a finger or a pen; not a right or middle click.
        if (e.button > 0) {
          return;
        }
        e.preventDefault();
        const handle = e.currentTarget;
        handle.setPointerCapture?.(e.pointerId);
        const area = handle.parentElement?.getBoundingClientRect();
        if (!area) {
          return;
        }
        document.body.classList.add(wide ? 'resizing-x' : 'resizing-y');
        const move = (ev: PointerEvent) =>
          set(wide ? ev.clientX - area.left : ev.clientY - area.top);
        const up = () => {
          handle.removeEventListener('pointermove', move);
          handle.removeEventListener('pointerup', up);
          handle.removeEventListener('pointercancel', up);
          document.body.classList.remove('resizing-x', 'resizing-y');
        };
        handle.addEventListener('pointermove', move);
        handle.addEventListener('pointerup', up);
        handle.addEventListener('pointercancel', up);
      }}
      onDoubleClick={() =>
        setLayout(
          wide
            ? { listWidth: DEFAULT_LAYOUT.listWidth }
            : { listHeight: DEFAULT_LAYOUT.listHeight },
        )
      }
      onKeyDown={(e) => {
        const less = wide ? 'ArrowLeft' : 'ArrowUp';
        const more = wide ? 'ArrowRight' : 'ArrowDown';
        if (e.key === less || e.key === more) {
          e.preventDefault();
          e.stopPropagation();
          set(size + (e.key === more ? STEP : -STEP));
        } else if (e.key === 'Home' || e.key === 'End') {
          e.preventDefault();
          const { min: lo, max: hi } = limits();
          set(e.key === 'Home' ? lo : hi);
        }
      }}
    />
  );
}
